import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../utils/axios";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import { toast } from "react-hot-toast";

const PAGE_SIZE = 15;

const emptyVendor = {
  code: "",
  name: "",
  vendor_category_id: "",
  vendor_type_id: "",
  contact_person: "",
  email: "",
  phone: "",
  alternate_phone: "",
  website: "",
  address: "",
  city: "",
  state: "",
  postal_code: "",
  country: "India",
  gstin: "",
  pan: "",
  notes: "",
  status: "Active",
};

const getErrorMessage = (error) => {
  const validationErrors = error?.response?.data?.errors;

  if (validationErrors) {
    const firstError = Object.values(validationErrors).flat()[0];
    if (firstError) return firstError;
  }

  return (
    error?.response?.data?.message ||
    error?.message ||
    "Something went wrong. Please try again."
  );
};

const unwrapList = (response) => {
  const payload = response?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;

  return [];
};

const normalizeVendor = (vendor) => {
  const addressParts = [
    vendor.address,
    vendor.city,
    vendor.state,
    vendor.postal_code,
    vendor.country,
  ].filter(Boolean);

  return {
    ...vendor,
    id: vendor.id,
    code: vendor.code || `VEN-${vendor.id}`,
    name: vendor.name || "",
    vendor_category_id: String(
      vendor.vendor_category_id ?? vendor.category?.id ?? ""
    ),
    vendor_type_id: String(vendor.vendor_type_id ?? vendor.type?.id ?? ""),
    category: vendor.category?.name || vendor.category_name || "—",
    type: vendor.type?.name || vendor.type_name || "—",
    contact_person: vendor.contact_person || vendor.contactPerson || "",
    contactPerson: vendor.contact_person || vendor.contactPerson || "",
    email: vendor.email || "",
    phone: vendor.phone || "",
    alternate_phone: vendor.alternate_phone || "",
    website: vendor.website || "",
    raw_address: vendor.address || "",
    address: addressParts.join(", ") || "—",
    city: vendor.city || "",
    state: vendor.state || "",
    postal_code: vendor.postal_code || "",
    country: vendor.country || "India",
    gstin: vendor.gstin || "",
    pan: vendor.pan || "",
    notes: vendor.notes || "",
    status: vendor.status || "Inactive",
  };
};

const VendorManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [form, setForm] = useState(emptyVendor);
  const [formErrors, setFormErrors] = useState({});

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load Categories & Types for filtering and creation dropdowns
  const loadCategoriesAndTypes = useCallback(async () => {
    try {
      const [categoryResponse, typeResponse] = await Promise.all([
        axiosInstance.get("/vendor-categories", { params: { per_page: 200 } }),
        axiosInstance.get("/vendor-types", { params: { per_page: 200 } }),
      ]);

      setCategories(unwrapList(categoryResponse));
      setTypes(unwrapList(typeResponse));
    } catch (requestError) {
      console.error("Failed to load vendor categories/types:", requestError);
    }
  }, []);

  // Load vendors list from API
  const loadVendors = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        page,
        per_page: PAGE_SIZE,
      };

      if (search.trim()) params.search = search.trim();
      if (categoryFilter !== "all") {
        params.vendor_category_id = categoryFilter;
      }
      if (typeFilter !== "all") {
        params.vendor_type_id = typeFilter;
      }
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      const response = await axiosInstance.get("/vendors", {
        params,
      });

      const payload = response?.data;
      const paginator = payload?.data;

      const rows = Array.isArray(paginator)
        ? paginator
        : Array.isArray(paginator?.data)
          ? paginator.data
          : [];

      setVendors(rows.map(normalizeVendor));
      setTotal(
        Number(
          paginator?.total ?? payload?.meta?.total ?? rows.length
        )
      );
      setLastPage(
        Math.max(
          1,
          Number(
            paginator?.last_page ?? payload?.meta?.last_page ?? 1
          )
        )
      );
    } catch (requestError) {
      console.error("Failed to load vendors:", requestError);
      setVendors([]);
      setTotal(0);
      setLastPage(1);
      const msg = getErrorMessage(requestError);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [page, search, categoryFilter, typeFilter, statusFilter]);

  useEffect(() => {
    loadCategoriesAndTypes();
  }, [loadCategoriesAndTypes]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const activeCount = useMemo(
    () => vendors.filter((vendor) => vendor.status === "Active").length,
    [vendors]
  );

  const resetFilters = () => {
    setSearch("");
    setCategoryFilter("all");
    setTypeFilter("all");
    setStatusFilter("all");
    setPage(1);
  };

  // Open modal for creating a new vendor
  const openCreate = async () => {
    setModalMode("create");
    setSelectedVendor(null);
    setFormErrors({});
    setNotice("");

    let nextCode = "";
    try {
      const res = await axiosInstance.get("/vendors/next-code");
      if (res?.data?.code) {
        nextCode = res.data.code;
      }
    } catch (e) {
      nextCode = `VEN-${String(total + 1).padStart(4, "0")}`;
    }

    setForm({
      ...emptyVendor,
      code: nextCode,
    });
    setShowModal(true);
  };

  // Open modal for editing an existing vendor
  const openEdit = (vendor) => {
    setModalMode("edit");
    setSelectedVendor(vendor);
    setForm({
      code: vendor.code || "",
      name: vendor.name || "",
      vendor_category_id: String(vendor.vendor_category_id || ""),
      vendor_type_id: String(vendor.vendor_type_id || ""),
      contact_person: vendor.contact_person || vendor.contactPerson || "",
      email: vendor.email || "",
      phone: vendor.phone || "",
      alternate_phone: vendor.alternate_phone || "",
      website: vendor.website || "",
      address:
        vendor.raw_address || (vendor.address !== "—" ? vendor.address : "") || "",
      city: vendor.city || "",
      state: vendor.state || "",
      postal_code: vendor.postal_code || "",
      country: vendor.country || "India",
      gstin: vendor.gstin || "",
      pan: vendor.pan || "",
      notes: vendor.notes || "",
      status: vendor.status || "Active",
    });
    setFormErrors({});
    setNotice("");
    setShowModal(true);
  };

  // Open view details modal
  const openView = async (vendor) => {
    try {
      const response = await axiosInstance.get(`/vendors/${vendor.id}`);
      const record = response?.data?.data || response?.data;
      setSelectedVendor(normalizeVendor(record));
    } catch (requestError) {
      toast.error(getErrorMessage(requestError));
    }
  };

  // Toggle vendor Active/Inactive status directly from the table
  const handleToggleStatus = async (vendor) => {
    try {
      const response = await axiosInstance.patch(`/vendors/${vendor.id}/status`);
      const updated = response?.data?.data;
      const nextStatus = updated?.status || (vendor.status === "Active" ? "Inactive" : "Active");

      setVendors((prev) =>
        prev.map((v) => (v.id === vendor.id ? { ...v, status: nextStatus } : v))
      );
      toast.success(
        response?.data?.message || `Vendor status updated to ${nextStatus}.`
      );
    } catch (toggleError) {
      toast.error(getErrorMessage(toggleError));
    }
  };

  const updateForm = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    setFormErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  // Save (Create or Update) Vendor
  const saveVendor = async (event) => {
    event.preventDefault();
    setFormErrors({});
    setNotice("");

    const errors = {};
    if (!form.name?.trim()) {
      errors.name = "Vendor name is required.";
    }
    if (!form.vendor_category_id) {
      errors.vendor_category_id = "Please select a vendor category.";
    }
    if (!form.vendor_type_id) {
      errors.vendor_type_id = "Please select a vendor type.";
    }
    if (!form.code?.trim()) {
      errors.code = "Vendor code is required.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Please fill in all required fields.");
      return;
    }

    setSaving(true);

    const safeTrim = (v) => (typeof v === "string" ? v.trim() : "") || null;

    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      vendor_category_id: Number(form.vendor_category_id),
      vendor_type_id: Number(form.vendor_type_id),
      contact_person: safeTrim(form.contact_person),
      email: safeTrim(form.email),
      phone: safeTrim(form.phone),
      alternate_phone: safeTrim(form.alternate_phone),
      website: safeTrim(form.website),
      address: safeTrim(form.address),
      city: safeTrim(form.city),
      state: safeTrim(form.state),
      postal_code: safeTrim(form.postal_code),
      country: safeTrim(form.country) || "India",
      gstin: safeTrim(form.gstin)?.toUpperCase() || null,
      pan: safeTrim(form.pan)?.toUpperCase() || null,
      notes: safeTrim(form.notes),
      status: form.status || "Active",
    };

    try {
      if (modalMode === "edit" && selectedVendor?.id) {
        await axiosInstance.put(`/vendors/${selectedVendor.id}`, payload);
        toast.success("Vendor updated successfully.");
      } else {
        await axiosInstance.post("/vendors", payload);
        toast.success("Vendor created successfully.");
      }

      setShowModal(false);
      setSelectedVendor(null);
      await loadVendors();
    } catch (requestError) {
      console.error("Failed to save vendor:", requestError);
      const serverErrors = requestError?.response?.data?.errors;
      if (serverErrors) {
        setFormErrors(serverErrors);
      }
      const msg = getErrorMessage(requestError);
      setNotice(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Delete vendor
  const deleteVendor = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      await axiosInstance.delete(`/vendors/${deleteTarget.id}`);
      setDeleteTarget(null);
      toast.success("Vendor deleted successfully.");

      if (vendors.length === 1 && page > 1) {
        setPage((current) => current - 1);
      } else {
        await loadVendors();
      }
    } catch (requestError) {
      toast.error(getErrorMessage(requestError));
    } finally {
      setDeleting(false);
    }
  };

  // Export vendors as CSV
  const exportCsv = () => {
    if (!vendors.length) {
      toast.error("No vendor data available to export.");
      return;
    }

    const columns = [
      ["Vendor Code", "code"],
      ["Vendor Name", "name"],
      ["Category", "category"],
      ["Type", "type"],
      ["Contact Person", "contactPerson"],
      ["Email", "email"],
      ["Phone", "phone"],
      ["City", "city"],
      ["State", "state"],
      ["GSTIN", "gstin"],
      ["PAN", "pan"],
      ["Status", "status"],
    ];

    const escapeCsv = (value) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;

    const csv = [
      columns.map(([heading]) => escapeCsv(heading)).join(","),
      ...vendors.map((vendor) =>
        columns.map(([, key]) => escapeCsv(vendor[key])).join(",")
      ),
    ].join("\r\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `vendors_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Vendor directory exported successfully.");
  };

  const fieldClass =
    "w-full min-w-0 px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all";

  const inputField = (label, field, options = {}) => (
    <div key={field} className={options.full ? "md:col-span-2" : ""}>
      <label className="block text-sm font-medium text-foreground mb-1.5">
        {label}
        {options.required ? <span className="text-destructive ml-1">*</span> : ""}
      </label>
      {options.textarea ? (
        <textarea
          rows={options.rows || 3}
          value={form[field] ?? ""}
          onChange={(event) => updateForm(field, event.target.value)}
          className={fieldClass}
          placeholder={options.placeholder || ""}
        />
      ) : options.options ? (
        <select
          value={form[field] ?? ""}
          onChange={(event) => updateForm(field, event.target.value)}
          className={fieldClass}
        >
          <option value="">Select {label.toLowerCase()}</option>
          {options.options.map((item) => (
            <option key={item.id} value={String(item.id)}>
              {item.name}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={options.type || "text"}
          value={form[field] ?? ""}
          onChange={(event) => updateForm(field, event.target.value)}
          className={fieldClass}
          placeholder={options.placeholder || ""}
          required={options.required || false}
        />
      )}
      {formErrors[field] && (
        <p className="text-xs text-destructive mt-1 font-medium">
          {Array.isArray(formErrors[field])
            ? formErrors[field][0]
            : formErrors[field]}
        </p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="flex">
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((value) => !value)}
        />

        <main
          className={`flex-1 min-w-0 transition-layout ${
            sidebarCollapsed ? "ml-16" : "ml-72"
          } mt-16`}
        >
          <div className="p-4 md:p-6 xl:p-8 max-w-[1800px] mx-auto">
            <Breadcrumb />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-7">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 shrink-0 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shadow-sm">
                  <Icon name="Building2" size={26} />
                </div>
                <div className="min-w-0">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                    Vendor Management
                  </h1>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Manage certified suppliers, contracts, primary contacts, and vendor catalog.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={exportCsv}
                >
                  Export CSV
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={openCreate}
                >
                  Add Vendor
                </Button>
              </div>
            </div>

            {error && (
              <div className="mb-5 flex items-start justify-between gap-4 rounded-xl border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
                <div className="flex items-center gap-2">
                  <Icon name="AlertCircle" size={18} />
                  <span>{error}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setError("")}
                  aria-label="Dismiss error"
                  className="hover:opacity-75"
                >
                  <Icon name="X" size={17} />
                </button>
              </div>
            )}

            {/* Summary statistics cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
              {[
                {
                  label: "Total Vendors",
                  value: total,
                  subtitle: "Registered vendor partners",
                  icon: "Building2",
                  color: "text-primary bg-primary/10",
                },
                {
                  label: "Active on This Page",
                  value: activeCount,
                  subtitle: "Active vendors currently listed",
                  icon: "CircleCheck",
                  color: "text-emerald-600 bg-emerald-500/10 dark:text-emerald-400",
                },
                {
                  label: "Vendor Categories",
                  value: categories.length,
                  subtitle: "Defined business categories",
                  icon: "Tags",
                  color: "text-blue-600 bg-blue-500/10 dark:text-blue-400",
                },
                {
                  label: "Vendor Types",
                  value: types.length,
                  subtitle: "Contract & supplier types",
                  icon: "BriefcaseBusiness",
                  color: "text-amber-600 bg-amber-500/10 dark:text-amber-400",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="min-w-0 bg-card border border-border rounded-2xl p-5 shadow-xs"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {item.label}
                      </p>
                      <p className="text-2xl font-bold text-foreground mt-2">
                        {item.value}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {item.subtitle}
                      </p>
                    </div>
                    <div
                      className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center ${item.color}`}
                    >
                      <Icon name={item.icon} size={22} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Supplier directory table container */}
            <section className="bg-card border border-border rounded-2xl overflow-hidden shadow-xs">
              <div className="p-5 md:p-6 border-b border-border">
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-foreground">
                      Supplier Directory
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Search, view, and manage all corporate supplier profiles.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 w-full xl:w-auto xl:min-w-[720px]">
                    <div className="relative sm:col-span-2 xl:col-span-1">
                      <Icon
                        name="Search"
                        size={17}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        type="search"
                        value={search}
                        onChange={(event) => {
                          setSearch(event.target.value);
                          setPage(1);
                        }}
                        placeholder="Search vendors..."
                        className={`${fieldClass} pl-9`}
                      />
                    </div>

                    <select
                      value={categoryFilter}
                      onChange={(event) => {
                        setCategoryFilter(event.target.value);
                        setPage(1);
                      }}
                      className={fieldClass}
                    >
                      <option value="all">All Categories</option>
                      {categories.map((item) => (
                        <option key={item.id} value={String(item.id)}>
                          {item.name}
                        </option>
                      ))}
                    </select>

                    <select
                      value={typeFilter}
                      onChange={(event) => {
                        setTypeFilter(event.target.value);
                        setPage(1);
                      }}
                      className={fieldClass}
                    >
                      <option value="all">All Types</option>
                      {types.map((item) => (
                        <option key={item.id} value={String(item.id)}>
                          {item.name}
                        </option>
                      ))}
                    </select>

                    <select
                      value={statusFilter}
                      onChange={(event) => {
                        setStatusFilter(event.target.value);
                        setPage(1);
                      }}
                      className={fieldClass}
                    >
                      <option value="all">All Statuses</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4 flex justify-between items-center text-xs text-muted-foreground">
                  <span>
                    Showing {vendors.length} of {total} registered vendors
                  </span>
                  {(search || categoryFilter !== "all" || typeFilter !== "all" || statusFilter !== "all") && (
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="text-primary hover:underline font-medium"
                    >
                      Reset filters
                    </button>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1050px] table-fixed">
                  <colgroup>
                    <col className="w-[22%]" />
                    <col className="w-[18%]" />
                    <col className="w-[20%]" />
                    <col className="w-[20%]" />
                    <col className="w-[10%]" />
                    <col className="w-[10%]" />
                  </colgroup>

                  <thead>
                    <tr className="bg-muted/30 border-b border-border">
                      {[
                        "Vendor",
                        "Category / Type",
                        "Primary Contact",
                        "Location",
                        "Status",
                        "Actions",
                      ].map((heading) => (
                        <th
                          key={heading}
                          className="px-4 py-3.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-16 text-center">
                          <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
                            <Icon name="LoaderCircle" size={20} className="animate-spin text-primary" />
                            Loading vendors from server...
                          </div>
                        </td>
                      </tr>
                    ) : vendors.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-16 text-center">
                          <div className="w-12 h-12 rounded-2xl bg-muted/60 flex items-center justify-center mx-auto text-muted-foreground">
                            <Icon name="SearchX" size={26} />
                          </div>
                          <h3 className="font-semibold text-foreground mt-3">
                            No vendors found
                          </h3>
                          <p className="text-xs text-muted-foreground mt-1">
                            Try adjusting your filters or click "Add Vendor" to create one.
                          </p>
                        </td>
                      </tr>
                    ) : (
                      vendors.map((vendor) => (
                        <tr
                          key={vendor.id}
                          className="border-b border-border last:border-b-0 hover:bg-muted/20 transition-colors"
                        >
                          {/* Vendor info */}
                          <td className="px-4 py-4 align-middle">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">
                                {vendor.code?.slice(-4) || "VEN"}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-sm text-foreground truncate">
                                  {vendor.name}
                                </p>
                                <span className="inline-block font-mono text-xs text-muted-foreground mt-0.5">
                                  {vendor.code}
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* Category and Type */}
                          <td className="px-4 py-4 align-middle">
                            <p className="text-sm font-medium text-foreground truncate">
                              {vendor.category}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {vendor.type}
                            </p>
                          </td>

                          {/* Contact */}
                          <td className="px-4 py-4 align-middle">
                            <p className="text-sm font-medium text-foreground truncate">
                              {vendor.contactPerson || "—"}
                            </p>
                            {vendor.email && (
                              <a
                                href={`mailto:${vendor.email}`}
                                className="text-xs text-primary hover:underline block truncate mt-0.5"
                              >
                                {vendor.email}
                              </a>
                            )}
                            {vendor.phone && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {vendor.phone}
                              </p>
                            )}
                          </td>

                          {/* Location */}
                          <td className="px-4 py-4 align-middle text-xs text-muted-foreground whitespace-normal break-words">
                            {vendor.address}
                          </td>

                          {/* Status */}
                          <td className="px-4 py-4 align-middle">
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(vendor)}
                              title="Click to toggle status"
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold cursor-pointer transition-all hover:scale-105 ${
                                vendor.status === "Active"
                                  ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400"
                                  : "bg-muted text-muted-foreground border-border"
                              }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  vendor.status === "Active"
                                    ? "bg-emerald-500"
                                    : "bg-muted-foreground"
                                }`}
                              />
                              {vendor.status}
                            </button>
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-4 align-middle">
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                title="View details"
                                onClick={() => openView(vendor)}
                                className="w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
                              >
                                <Icon name="Eye" size={16} />
                              </button>
                              <button
                                type="button"
                                title="Edit vendor"
                                onClick={() => openEdit(vendor)}
                                className="w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
                              >
                                <Icon name="Pencil" size={16} />
                              </button>
                              <button
                                type="button"
                                title="Delete vendor"
                                onClick={() => setDeleteTarget(vendor)}
                                className="w-8 h-8 rounded-lg hover:bg-destructive/10 text-destructive flex items-center justify-center transition-colors"
                              >
                                <Icon name="Trash2" size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-5 py-4 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-muted/10">
                <p className="text-xs text-muted-foreground">
                  Showing page {page} of {lastPage} ({total} total vendors)
                </p>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    className="px-3 py-1.5 border border-border rounded-lg text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={page >= lastPage || loading}
                    onClick={() =>
                      setPage((current) => Math.min(lastPage, current + 1))
                    }
                    className="px-3 py-1.5 border border-border rounded-lg text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* Add / Edit Vendor Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 md:p-6 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="relative w-full max-w-3xl max-h-[92vh] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-foreground">
                  {modalMode === "edit" ? "Edit Vendor Details" : "Add New Supplier / Vendor"}
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Save vendor profile, classification, primary contact, and tax credentials.
                </p>
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <form
              onSubmit={saveVendor}
              className="flex-1 min-h-0 overflow-y-auto"
            >
              <div className="p-6 space-y-6">
                {notice && (
                  <div className="p-3 rounded-xl border border-destructive/20 bg-destructive/10 text-xs text-destructive flex items-center gap-2">
                    <Icon name="AlertCircle" size={16} />
                    <span>{notice}</span>
                  </div>
                )}

                {/* Classification Section */}
                <section>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Vendor Identification & Category
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-1">
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Vendor Code <span className="text-destructive">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={form.code ?? ""}
                          onChange={(e) => updateForm("code", e.target.value.toUpperCase())}
                          placeholder="VEN-0001"
                          className={`${fieldClass} font-mono uppercase`}
                          required
                        />
                        {modalMode === "create" && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const res = await axiosInstance.get("/vendors/next-code");
                                if (res?.data?.code) updateForm("code", res.data.code);
                              } catch (e) {
                                // ignore
                              }
                            }}
                            title="Generate next available code"
                            className="px-3 py-2 border border-border rounded-lg text-xs hover:bg-muted font-medium shrink-0"
                          >
                            Auto
                          </button>
                        )}
                      </div>
                      {formErrors.code && (
                        <p className="text-xs text-destructive mt-1 font-medium">
                          {Array.isArray(formErrors.code) ? formErrors.code[0] : formErrors.code}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Status <span className="text-destructive">*</span>
                      </label>
                      <select
                        value={form.status}
                        onChange={(e) => updateForm("status", e.target.value)}
                        className={fieldClass}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>

                    {inputField("Vendor Name", "name", {
                      required: true,
                      full: true,
                      placeholder: "e.g. Dell Technologies India Pvt Ltd",
                    })}

                    {inputField("Vendor Category", "vendor_category_id", {
                      required: true,
                      options: categories,
                    })}

                    {inputField("Vendor Type", "vendor_type_id", {
                      required: true,
                      options: types,
                    })}
                  </div>
                </section>

                {/* Primary Contact Section */}
                <section className="pt-2 border-t border-border">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {inputField("Contact Person", "contact_person", {
                      placeholder: "e.g. Rajesh Sharma",
                    })}
                    {inputField("Email Address", "email", {
                      type: "email",
                      placeholder: "sales@supplier.com",
                    })}
                    {inputField("Phone Number", "phone", {
                      placeholder: "+91 98765 43210",
                    })}
                    {inputField("Alternate Phone", "alternate_phone", {
                      placeholder: "+91 98765 43211",
                    })}
                    {inputField("Official Website", "website", {
                      placeholder: "https://www.supplier.com",
                    })}
                    {inputField("Country", "country", {
                      placeholder: "India",
                    })}
                    {inputField("Street Address", "address", {
                      full: true,
                      textarea: true,
                      rows: 2,
                      placeholder: "Corporate office / facility address",
                    })}
                    {inputField("City", "city", {
                      placeholder: "Bengaluru",
                    })}
                    {inputField("State", "state", {
                      placeholder: "Karnataka",
                    })}
                    {inputField("Postal Code", "postal_code", {
                      placeholder: "560001",
                    })}
                  </div>
                </section>

                {/* Compliance & Tax Information */}
                <section className="pt-2 border-t border-border">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    Tax & Business Identification
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {inputField("GSTIN Number", "gstin", {
                      placeholder: "29AAACD1234F1Z5",
                    })}
                    {inputField("PAN Number", "pan", {
                      placeholder: "AAACD1234F",
                    })}
                    {inputField("Internal Notes", "notes", {
                      full: true,
                      textarea: true,
                      rows: 3,
                      placeholder: "Contract notes, discount tiers, SLA agreements, or payment terms...",
                    })}
                  </div>
                </section>
              </div>

              {/* Modal footer */}
              <div className="sticky bottom-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-3 shadow-lg">
                <Button
                  type="button"
                  variant="outline"
                  disabled={saving}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  iconName={saving ? "LoaderCircle" : "Save"}
                  iconPosition="left"
                >
                  {saving
                    ? "Saving Vendor..."
                    : modalMode === "edit"
                      ? "Update Vendor"
                      : "Save Vendor"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vendor details modal */}
      {selectedVendor && !showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-2xl p-6">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                  <Icon name="Building2" size={22} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-foreground">
                    {selectedVendor.name}
                  </h2>
                  <p className="text-xs font-mono text-muted-foreground">
                    {selectedVendor.code}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedVendor(null)}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                ["Vendor Name", selectedVendor.name],
                ["Vendor Code", selectedVendor.code],
                ["Category", selectedVendor.category],
                ["Type", selectedVendor.type],
                ["Contact Person", selectedVendor.contactPerson],
                ["Email", selectedVendor.email],
                ["Phone", selectedVendor.phone],
                ["Alternate Phone", selectedVendor.alternate_phone],
                ["Website", selectedVendor.website],
                ["GSTIN", selectedVendor.gstin],
                ["PAN", selectedVendor.pan],
                ["Status", selectedVendor.status],
                ["Full Address", selectedVendor.address],
                ["Notes", selectedVendor.notes],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className={`min-w-0 ${
                    label === "Full Address" || label === "Notes"
                      ? "sm:col-span-2"
                      : ""
                  }`}
                >
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {label}
                  </p>
                  <p className="text-sm font-semibold text-foreground mt-1 break-words">
                    {value || "—"}
                  </p>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-border flex justify-between items-center">
              <Button
                variant="outline"
                iconName="Pencil"
                iconPosition="left"
                onClick={() => {
                  const current = selectedVendor;
                  setSelectedVendor(null);
                  openEdit(current);
                }}
              >
                Edit
              </Button>
              <Button
                variant="default"
                onClick={() => setSelectedVendor(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="relative w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-4">
              <Icon name="Trash2" size={28} />
            </div>
            <h2 className="text-lg font-bold text-foreground">
              Delete Vendor Profile?
            </h2>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Are you sure you want to delete{" "}
              <strong className="text-foreground">{deleteTarget.name}</strong> (
              <span className="font-mono">{deleteTarget.code}</span>)? This action
              cannot be undone.
            </p>
            <div className="flex justify-center gap-3 mt-6">
              <Button
                variant="outline"
                disabled={deleting}
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleting}
                iconName={deleting ? "LoaderCircle" : "Trash2"}
                iconPosition="left"
                onClick={deleteVendor}
              >
                {deleting ? "Deleting..." : "Delete Vendor"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;
